import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Card, CardContent, CardHeader, CardTitle } from "../common/card";

const ServiceCard = ({ icon, title, description }) => (
 <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6">
   <div className="text-3xl mb-4">{icon}</div>
   <h3 className="text-xl font-semibold mb-3 text-gray-800">{title}</h3>
   <p className="text-gray-600">{description}</p>
 </div>
);

const FeatureCard = ({ icon, title, description }) => (
 <div className="space-y-4 p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-300">
   <h3 className="text-xl font-semibold flex items-center gap-2">
     <span>{icon}</span>
     {title}
   </h3>
   <p className="text-gray-600">{description}</p>
 </div>
);

const SectionIndicator = ({ currentSection, totalSections }) => (
 <div className="fixed right-10 top-1/2 transform -translate-y-1/2 space-y-2">
   {[...Array(totalSections)].map((_, index) => (
     <div
       key={index}
       className={`w-2 h-2 rounded-full cursor-pointer transition-all duration-300 ${
         currentSection === index ? 'bg-blue-600 w-3 h-3' : 'bg-gray-300'
       }`}
       onClick={() => {
         const element = document.getElementById(`section-${index}`);
         element?.scrollIntoView({ behavior: 'smooth' });
       }}
     />
   ))}
 </div>
);

const LandingPage = () => {
 const [currentSection, setCurrentSection] = useState(0);
 const totalSections = 4;
 const [featuresRef, featuresInView] = useInView({
   triggerOnce: true,
   threshold: 0.1
 });

 useEffect(() => {
   const handleScroll = () => {
     const sections = document.querySelectorAll('section[id^="section-"]');
     const scrollPosition = window.scrollY + window.innerHeight / 2;

     sections.forEach((section, index) => {
       const top = section.offsetTop;
       const height = section.offsetHeight;

       if (scrollPosition >= top && scrollPosition < top + height) {
         setCurrentSection(index);
       }
     });
   };

   window.addEventListener('scroll', handleScroll);
   return () => window.removeEventListener('scroll', handleScroll);
 }, []);

 const services = [
   {
     icon: "💼",
     title: "회계/재무",
     description: "자산 구매, 비용 처리, 재무 활동 등 회계 업무를 AI가 도와드립니다."
   },
   {
     icon: "📊",
     title: "업무 자동화",
     description: "PDF 분석, 이메일 작성 등 반복적인 업무를 AI가 처리해드립니다."
   },
   {
     icon: "🤖",
     title: "AI 어시스턴트",
     description: "업무 전반에 걸쳐 AI가 24시간 당신의 든든한 파트너가 되어드립니다."
   },
   {
     icon: "🚀",
     title: "생산성 향상",
     description: "단순 반복 업무는 AI에게 맡기고, 핵심 업무에 집중하세요."
   }
 ];

 const features = [
   {
     icon: "⚡️",
     title: "빠른 업무 처리",
     description: "복잡한 업무도 AI가 순식간에 처리해드립니다."
   },
   {
     icon: "🎯",
     title: "정확한 결과",
     description: "검증된 AI 모델이 정확하고 신뢰할 수 있는 결과를 제공합니다."
   },
   {
     icon: "📚",
     title: "지속적인 학습",
     description: "사용자 피드백을 통해 계속해서 발전하는 AI 서비스를 제공합니다."
   }
 ];

 return (
   <div className="min-h-screen">
     <SectionIndicator currentSection={currentSection} totalSections={totalSections} />

     {/* Hero Section */}
     <section id="section-0" className="h-screen flex items-center justify-center">
       <div className="max-w-4xl mx-auto text-center mb-[5%] px-4">
         <h1 className="text-5xl md:text-7xl font-bold mb-8">
           FairNex
         </h1>
         <p className="text-xl md:text-2xl text-gray-600 mb-6">
           AI로 더 스마트하게, 더 효율적으로 일하세요
         </p>
         <p className="text-lg md:text-xl text-gray-500 mb-12">
           24시간 당신의 업무를 도와주는 AI 에이전트
         </p>
         <Link 
           to="/services"
           className="inline-block px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-300"
         >
           FairNex 시작하기
         </Link>
       </div>
     </section>

     {/* Features Section */}
     <section id="section-1" className="h-screen flex items-center justify-center bg-gray-50">
       <motion.div 
         ref={featuresRef}
         className="max-w-7xl mx-auto px-4"
         initial={{ opacity: 0, y: 50 }}
         animate={featuresInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
         transition={{ duration: 0.5 }}
       >
         <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
           <Card className="hover:shadow-lg transition-shadow duration-300">
             <CardHeader>
               <CardTitle className="flex items-center">
                 <span className="text-2xl mr-2">⚡️</span>
                 업무 자동화
               </CardTitle>
             </CardHeader>
             <CardContent>
               <p>반복적인 업무를 AI가 자동으로 처리하여 시간을 절약해드립니다.</p>
             </CardContent>
           </Card>
           <Card className="hover:shadow-lg transition-shadow duration-300">
             <CardHeader>
               <CardTitle className="flex items-center">
                 <span className="text-2xl mr-2">🎯</span>
                 정확한 결과
               </CardTitle>
             </CardHeader>
             <CardContent>
               <p>최신 AI 기술로 정확하고 신뢰할 수 있는 결과를 제공합니다.</p>
             </CardContent>
           </Card>
           <Card className="hover:shadow-lg transition-shadow duration-300">
             <CardHeader>
               <CardTitle className="flex items-center">
                 <span className="text-2xl mr-2">🤖</span>
                 Any time, Any where
               </CardTitle>
             </CardHeader>
             <CardContent>
               <p>언제 어디서나 AI 에이전트가 당신의 업무를 지원합니다.</p>
             </CardContent>
           </Card>
         </div>
       </motion.div>
     </section>

     {/* Services Section */}
     <section id="section-2" className="min-h-screen flex flex-col justify-center py-20">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">서비스 영역</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <ServiceCard key={index} {...service} />
            ))}
          </div>
        </div>
     </section>

     {/* Additional Features Section */}
     <section id="section-3" className="min-h-screen flex flex-col justify-center py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">주요 기능</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FeatureCard
              icon="🔄"
              title="업무 프로세스 자동화"
              description="복잡한 업무 프로세스를 AI가 자동으로 처리하여 효율성을 높입니다."
            />
            <FeatureCard
              icon="💡"
              title="스마트 분석"
              description="AI가 데이터를 분석하여 인사이트를 제공하고 의사결정을 돕습니다."
            />
            <FeatureCard
              icon="🔍"
              title="맞춤형 솔루션"
              description="각 업무 상황에 맞는 최적의 솔루션을 제안합니다."
            />
            <FeatureCard
              icon="📈"
              title="생산성 향상"
              description="반복 업무 자동화로 핵심 업무에 더 집중할 수 있습니다."
            />
          </div>
        </div>
      </section>
   </div>
 );
};

export default LandingPage;